import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebaseClient";
import {
  localBranchInfo,
  seedFirestoreBranchDocs,
  regionIds,
  RegionId,
  BranchConfig,
  FirestoreBranchDoc,
  buildBranchConfig,
} from "./branches";

export type BranchState =
  | { status: "loading" }
  | { status: "notfound" }
  | { status: "ready"; branch: BranchConfig };

// Firestoreへの問い合わせがこの時間内に終わらない場合は
// ローカルの初期値(seedFirestoreBranchDocs)で表示する。
const FETCH_TIMEOUT_MS = 4000;

const TIMEOUT = Symbol("timeout");
const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T | typeof TIMEOUT> =>
  Promise.race([
    promise,
    new Promise<typeof TIMEOUT>((resolve) => setTimeout(() => resolve(TIMEOUT), ms)),
  ]);

// 同一regionへの取得結果はメモリ内(resolvedCache)に加え、
// sessionStorageにも保存する。このサイトの内部リンクは
// window.location.href による「フルページ遷移」で実装されているため、
// メモリ上のキャッシュだけではページ遷移のたびに失われてしまう。
// sessionStorageに置くことで、同じ支店のページを何度移動しても
// Firestoreへ再取得しに行かないようにする。
// ただし登録サイト側でデータを更新した場合に反映が遅れすぎないよう、
// CACHE_TTL_MS を過ぎたキャッシュは期限切れとして無視し、再取得する。
const resolvedCache = new Map<RegionId, BranchConfig>();
const inFlightCache = new Map<RegionId, Promise<BranchConfig>>();

const CACHE_TTL_MS = 60 * 1000; // 1分

interface CachedEntry {
  branch: BranchConfig;
  fetchedAt: number;
}

const sessionCacheKey = (region: RegionId) => `branchData:${region}`;

const readSessionCache = (region: RegionId): BranchConfig | null => {
  try {
    const raw = window.sessionStorage.getItem(sessionCacheKey(region));
    if (!raw) return null;
    const entry = JSON.parse(raw) as CachedEntry;
    if (Date.now() - entry.fetchedAt > CACHE_TTL_MS) {
      window.sessionStorage.removeItem(sessionCacheKey(region));
      return null;
    }
    return entry.branch;
  } catch {
    return null; // プライベートブラウジング等でsessionStorageが使えない場合は無視
  }
};

const writeSessionCache = (region: RegionId, branch: BranchConfig) => {
  try {
    const entry: CachedEntry = { branch, fetchedAt: Date.now() };
    window.sessionStorage.setItem(sessionCacheKey(region), JSON.stringify(entry));
  } catch {
    // 容量オーバーや利用不可の場合は無視(メモリキャッシュのみで動作継続)
  }
};

const fetchBranch = (region: RegionId): Promise<BranchConfig> => {
  const cached = resolvedCache.get(region) ?? readSessionCache(region) ?? undefined;
  if (cached) {
    resolvedCache.set(region, cached);
    return Promise.resolve(cached);
  }

  const inFlight = inFlightCache.get(region);
  if (inFlight) return inFlight;

  const local = localBranchInfo[region];

  // Firestoreから正常に取得できた場合だけキャッシュに保存する。
  // タイムアウトやエラーで表示したフォールバック(ローカルの古い値)を
  // 「取得成功」と同じ扱いでキャッシュしてしまうと、一時的な通信不調が
  // 1回起きただけでTTLの間ずっと古いデータが表示され続けてしまうため。
  const promise = withTimeout(getDoc(doc(db, "branches", region)), FETCH_TIMEOUT_MS)
    .then((result) => {
      if (result === TIMEOUT) {
        console.warn(
          `Firestoreからbranches/${region}の応答がタイムアウトしたため、ローカルの初期値で表示します(キャッシュはしません)`,
        );
        return { branch: buildBranchConfig(local, seedFirestoreBranchDocs[region]), fromFirestore: false };
      }
      if (!result.exists()) {
        // ドキュメントが本当に存在しない(未作成)場合は取得成功として扱ってよい。
        return { branch: buildBranchConfig(local, seedFirestoreBranchDocs[region]), fromFirestore: true };
      }
      return { branch: buildBranchConfig(local, result.data() as FirestoreBranchDoc), fromFirestore: true };
    })
    .catch((error) => {
      console.warn(
        `Firestoreからbranches/${region}を取得できなかったため、ローカルの初期値で表示します(キャッシュはしません)`,
        error,
      );
      return { branch: buildBranchConfig(local, seedFirestoreBranchDocs[region]), fromFirestore: false };
    })
    .then(({ branch, fromFirestore }) => {
      if (fromFirestore) {
        resolvedCache.set(region, branch);
        writeSessionCache(region, branch);
      }
      inFlightCache.delete(region);
      return branch;
    });

  inFlightCache.set(region, promise);
  return promise;
};

// region を明示的に受け取る版。useParams に依存しないため、
// Route配下でないApp.tsxのヘッダー/フッターからも利用できる。
export const useBranchByRegion = (region: RegionId | null): BranchState => {
  const [state, setState] = useState<BranchState>(() => {
    if (!region) return { status: "notfound" };
    const cached = resolvedCache.get(region) ?? readSessionCache(region);
    if (cached) resolvedCache.set(region, cached);
    return cached ? { status: "ready", branch: cached } : { status: "loading" };
  });

  useEffect(() => {
    if (!region) {
      setState({ status: "notfound" });
      return;
    }

    const cached = resolvedCache.get(region) ?? readSessionCache(region);
    if (cached) {
      resolvedCache.set(region, cached);
      setState({ status: "ready", branch: cached });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });

    fetchBranch(region).then((branch) => {
      if (!cancelled) setState({ status: "ready", branch });
    });

    return () => {
      cancelled = true;
    };
  }, [region]);

  return state;
};

// ページコンポーネント用。URLの :region パラメータから取得する。
export const useBranch = (): BranchState => {
  const { region } = useParams<{ region: string }>();
  const validRegion =
    region && regionIds.includes(region as RegionId)
      ? (region as RegionId)
      : null;
  return useBranchByRegion(validRegion);
};

export { Navigate };
